import DisplayContainer from "./DisplayContainer.ts";
import { Graphics, Text } from "pixi.js";
import RhFontsUtil from "../utils/RhFontsUtil.ts";
import SoundManager from "../managers/SoundManager.ts";
import RhColors from "@/models/RhColors.ts";

export default class IconButton extends DisplayContainer {
    //--------------------------------------------------------------------------
    //
    //  Properties
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Public:
    //----------------------------------

    public bgAlpha: number;
    public bgColor: string;
    public bgHeight: number;
    public bgRadius: number;
    public bgWidth: number;
    public hasBg: boolean;
    public iconColor: string;
    public iconFontSize: number;
    public mouseOverSound: string;
    public strokeColor: string;
    public strokeThickness: number;

    //----------------------------------
    //  Private:
    //----------------------------------

    private bg: Graphics;
    private fontAlias: string;
    private icon: Text;
    private iconCode: string;

    //--------------------------------------------------------------------------
    //
    //  Constructor
    //
    //--------------------------------------------------------------------------

    constructor(iconCode: string, fontAlias = RhFontsUtil.rhd_v9_icons) {
        super();
        this.iconCode = iconCode;
        this.fontAlias = fontAlias;
        this._init();
    }

    //--------------------------------------------------------------------------
    //
    //  Methods
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Private:
    //----------------------------------

    private _init(): void {
        this.bgAlpha = 0.9;
        this.bgColor = RhColors.BTN_SECONDARY;
        this.bgWidth = 65;
        this.bgHeight = 65;
        this.bgRadius = 8;
        this.hasBg = true;
        this.iconColor = RhColors.BTN_PRIMARY;
        this.iconFontSize = 25;
        this.strokeColor = this.bgColor;
        this.strokeThickness = 2;
    }

    private addListeners(): void {
        this.onmouseout = this.handleRollout;
        this.onmouseover = this.handleRollover;
    }

    private createBg(): void {
        this.bg = new Graphics();
        //debugger;
        this.bg.roundRect(0, 0, this.bgWidth, this.bgHeight, this.bgRadius);
        this.bg.fill(this.bgColor);
        this.bg.stroke({ width: this.strokeThickness, color: this.strokeColor });
        this.bg.alpha = this.bgAlpha;
        this.addChild(this.bg);
    }

    private createIcon(): void {
        this.icon = new Text({
            text: this.iconCode,
            style: { fontFamily: this.fontAlias, fontSize: this.iconFontSize, fill: this.iconColor },
        });
        this.icon.resolution = this.appModel.devicePixelRatio;
        this.icon.anchor.set(0.5);
        if (!this.hasBg) {
            this.bgWidth = this.icon.width;
            this.bgHeight = this.icon.height;
            this.bgAlpha = 0.01;
            this.createBg();
        }
        this.icon.x = this.width / 2;
        this.icon.y = this.height / 2;
        this.addChild(this.icon);
    }

    private handleRollout = (): void => {
        return null;
    };

    private handleRollover = (): void => {
        if (this.mouseOverSound) {
            SoundManager.getInstance().playSound(this.mouseOverSound);
        }
    };

    private removeListeners(): void {
        this.onmouseout = null;
        this.onmouseover = null;
    }

    //----------------------------------
    //  Public:
    //----------------------------------

    public override create(creationCompleteCallback?: () => void, show = true, enabled = true): void {
        if (this.hasBg) {
            this.createBg();
        }
        this.createIcon();
        this.cursor = "pointer";
        this.eventMode = "static";
        this.addListeners();
        super.create(creationCompleteCallback, show, enabled);
    }

    public override destroy(): void {
        this.removeListeners();
        super.destroy();
    }

    public override enable(): void {
        super.enable();

        this.bg.alpha = this.bgAlpha;
        this.icon.alpha = 1;
        this.cursor = "pointer";
    }

    public override disable(): void {
        super.disable();

        this.bg.alpha = 0.7;
        this.icon.alpha = 0.5;
        this.cursor = "none";
    }

    public swapIcon(newIcon: string): void {
        this.icon.text = newIcon;
    }

    public spinIcon(start: boolean): void {
        if (start) {
            gsap.to(this.icon, { rotation: '360', duration: 100, ease: 'none', repeat: -1 });
        } else {
            this.icon.rotation = 0;
            gsap.killTweensOf(this.icon);
        }
    }
}
